import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import VerificationBadges from '../verification/VerificationBadges';

const ProfileManagement = ({ isOpen, onClose }) => {
  const { user, updateProfile, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // Profile data state
  const [profileData, setProfileData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phoneNumber: '',
    bio: '',
    location: '',
    dateOfBirth: '',
    gender: '',
    
    // Travel Information
    travelExperience: {
      level: 'beginner',
      countriesVisited: 0,
      preferredTravelStyle: [],
      safetyPriority: 'medium'
    },
    
    // Interests
    interests: [],
    languages: [],
    
    // Privacy Settings
    privacy: {
      profileVisibility: 'public',
      showRealName: false,
      showLocation: true,
      showAge: false,
      showReviewHistory: true,
      allowDirectMessages: true,
      allowFollowing: true,
      dataProcessing: {
        analytics: true,
        marketing: false,
        thirdPartySharing: false
      }
    },
    
    // Notification Preferences
    notifications: {
      email: {
        reviewResponses: true,
        safetyAlerts: true,
        weeklyDigest: true,
        marketing: false
      },
      push: {
        reviewResponses: true,
        safetyAlerts: true,
        nearbyUpdates: false
      },
      sms: {
        emergencyAlerts: false,
        verificationCodes: true
      }
    }
  });

  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');

  // Form validation
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Available options
  const travelStyles = [
    'solo', 'couple', 'family', 'group', 'business', 
    'adventure', 'cultural', 'relaxation', 'backpacking', 'luxury'
  ];

  const interestOptions = [
    'nightlife', 'dining', 'shopping', 'museums', 'parks', 'beaches',
    'hiking', 'sports', 'photography', 'architecture', 'history',
    'art', 'music', 'festivals', 'local-culture', 'adventure-sports'
  ];

  const languageOptions = [
    'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Chinese', 'Japanese', 'Korean', 'Arabic', 'Russian', 'Dutch',
    'Hindi', 'Turkish', 'Thai', 'Vietnamese', 'Other'
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleEscapeKey);
    
    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && user) {
      // Initialize form with user data
      setProfileData({
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        displayName: user.profile?.displayName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        bio: user.profile?.bio || '',
        location: user.profile?.location?.city || '',
        dateOfBirth: user.profile?.dateOfBirth ? new Date(user.profile.dateOfBirth).toISOString().split('T')[0] : '',
        gender: user.profile?.gender || '',
        travelExperience: {
          level: user.profile?.travelExperience?.level || 'beginner',
          countriesVisited: user.profile?.travelExperience?.countriesVisited || 0,
          preferredTravelStyle: user.profile?.travelExperience?.preferredTravelStyle || [],
          safetyPriority: user.profile?.travelExperience?.safetyPriority || 'medium'
        },
        interests: user.profile?.interests || [],
        languages: user.profile?.languages || [],
        privacy: {
          profileVisibility: user.privacy?.profileVisibility || 'public',
          showRealName: user.privacy?.showRealName || false,
          showLocation: user.privacy?.showLocation ?? true,
          showAge: user.privacy?.showAge || false,
          showReviewHistory: user.privacy?.showReviewHistory ?? true,
          allowDirectMessages: user.privacy?.allowDirectMessages ?? true,
          allowFollowing: user.privacy?.allowFollowing ?? true,
          dataProcessing: {
            analytics: user.privacy?.dataProcessing?.analytics ?? true,
            marketing: user.privacy?.dataProcessing?.marketing || false,
            thirdPartySharing: user.privacy?.dataProcessing?.thirdPartySharing || false
          }
        },
        notifications: {
          email: {
            reviewResponses: user.notifications?.email?.reviewResponses ?? true,
            safetyAlerts: user.notifications?.email?.safetyAlerts ?? true,
            weeklyDigest: user.notifications?.email?.weeklyDigest ?? true,
            marketing: user.notifications?.email?.marketing || false
          },
          push: {
            reviewResponses: user.notifications?.push?.reviewResponses ?? true,
            safetyAlerts: user.notifications?.push?.safetyAlerts ?? true,
            nearbyUpdates: user.notifications?.push?.nearbyUpdates || false
          },
          sms: {
            emergencyAlerts: user.notifications?.sms?.emergencyAlerts || false,
            verificationCodes: user.notifications?.sms?.verificationCodes ?? true
          }
        }
      });
      
      setAvatarPreview(user.profile?.avatar || '');
    }
  }, [isOpen, user]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child, grandchild] = field.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: grandchild ? {
            ...prev[parent][child],
            [grandchild]: value
          } : value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    console.log('🔄 ARRAY_CHANGE: Field:', field, 'Value:', value);
    console.log('🔄 ARRAY_CHANGE: Current array:', profileData[field]);
    
    const isNested = field.includes('.');
    
    setProfileData(prev => {
      if (isNested) {
        const [parentField, childField] = field.split('.');
        const currentArray = prev[parentField][childField];
        const newArray = currentArray.includes(value) 
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value];
          
        console.log('🔄 ARRAY_CHANGE: New nested array:', newArray);
        return {
          ...prev,
          [parentField]: {
            ...prev[parentField],
            [childField]: newArray
          }
        };
      } else {
        const newArray = prev[field].includes(value) 
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value];
          
        console.log('🔄 ARRAY_CHANGE: New array:', newArray);
        return {
          ...prev,
          [field]: newArray
        };
      }
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        setSaveStatus({ type: 'error', message: 'Please select an image file' });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setSaveStatus({ type: 'error', message: 'Image size must be less than 5MB' });
        return;
      }
      
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!profileData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (!profileData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (profileData.phoneNumber && !/^\+?[\d\s-()]+$/.test(profileData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (profileData.bio.length > 500) {
      newErrors.bio = 'Bio must be less than 500 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    console.log('📝 PROFILE_SAVE: Starting save process');
    console.log('📝 PROFILE_SAVE: Current profileData:', profileData);
    
    if (!validateForm()) {
      console.log('📝 PROFILE_SAVE: Form validation failed');
      setSaveStatus({ type: 'error', message: 'Please fix the errors below' });
      return;
    }
    
    console.log('📝 PROFILE_SAVE: Form validation passed');
    setIsSubmitting(true);
    setSaveStatus({ type: '', message: '' });
    
    try {
      // Prepare update data
      const updateData = {
        profile: {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          displayName: profileData.displayName,
          bio: profileData.bio,
          location: { city: profileData.location },
          dateOfBirth: profileData.dateOfBirth,
          gender: profileData.gender,
          travelExperience: profileData.travelExperience,
          interests: profileData.interests,
          languages: profileData.languages
        },
        phoneNumber: profileData.phoneNumber,
        privacy: profileData.privacy,
        notifications: profileData.notifications
      };
      
      console.log('📝 PROFILE_SAVE: Prepared update data:', updateData);
      
      // Handle avatar upload if changed
      if (avatarFile) {
        // In a real app, you'd upload to a file storage service
        // For now, we'll just use the preview URL
        updateData.profile.avatar = avatarPreview;
        console.log('📝 PROFILE_SAVE: Added avatar to update data');
      }
      
      console.log('📝 PROFILE_SAVE: Calling updateProfile...');
      await updateProfile(updateData);
      console.log('📝 PROFILE_SAVE: updateProfile completed successfully');
      
      setSaveStatus({ type: 'success', message: 'Profile updated successfully!' });
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSaveStatus({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('📝 PROFILE_SAVE: Error occurred:', error);
      setSaveStatus({ type: 'error', message: error.message || 'Failed to update profile' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const calculateProfileCompletion = () => {
    const fields = [
      profileData.firstName,
      profileData.lastName,
      profileData.displayName,
      profileData.bio,
      profileData.location,
      profileData.dateOfBirth,
      profileData.gender,
      avatarPreview
    ];
    
    const completedFields = fields.filter(field => field && field.toString().trim() !== '').length;
    return Math.round((completedFields / fields.length) * 100);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '0' : '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: isMobile ? '0' : '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: isMobile ? '100%' : '90%',
          maxWidth: '800px',
          height: isMobile ? '100vh' : '90vh',
          maxHeight: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: isMobile ? '12px' : '15px',
              right: isMobile ? '12px' : '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              borderRadius: '50%',
              backgroundColor: '#f5f5f5',
              backgroundImage: avatarPreview ? `url(${avatarPreview})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
              border: '2px solid #e0e0e0'
            }}>
              {!avatarPreview && (user?.profile?.displayName || user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', color: '#333' }}>
                Profile Settings
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <VerificationBadges user={user} size="small" />
                <span style={{ 
                  fontSize: '12px', 
                  color: '#4CAF50',
                  fontWeight: '600'
                }}>
                  {calculateProfileCompletion()}% complete
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Save Status */}
        {saveStatus.message && (
          <div style={{
            padding: '12px 24px',
            backgroundColor: saveStatus.type === 'success' ? '#e8f5e8' : '#ffebee',
            color: saveStatus.type === 'success' ? '#2e7d32' : '#c62828',
            fontSize: '14px',
            borderBottom: '1px solid #e0e0e0'
          }}>
            {saveStatus.message}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9',
          overflowX: 'auto'
        }}>
          {[
            { id: 'basic', label: 'Basic Info', icon: '👤' },
            { id: 'travel', label: 'Travel', icon: '✈️' },
            { id: 'privacy', label: 'Privacy', icon: '🔒' },
            { id: 'notifications', label: 'Notifications', icon: '🔔' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: isMobile ? 'none' : 1,
                minWidth: isMobile ? '120px' : 'auto',
                padding: isMobile ? '8px 12px' : '12px 8px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#2196F3' : '#666',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #2196F3' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                justifyContent: 'center'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: isMobile ? '16px' : '24px'
        }}>
          {activeTab === 'basic' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Avatar Upload */}
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                  backgroundImage: avatarPreview ? `url(${avatarPreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  fontSize: '48px',
                  fontWeight: 'bold',
                  border: '4px solid #e0e0e0',
                  margin: '0 auto 16px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {!avatarPreview && (user?.profile?.displayName || user?.username || 'U').charAt(0).toUpperCase()}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Change Photo
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  Click to upload a new profile photo (max 5MB)
                </p>
              </div>

              {/* Basic Information */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={profileData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${errors.displayName ? '#f44336' : '#ddd'}`,
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                  {errors.displayName && (
                    <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                      {errors.displayName}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${errors.email ? '#f44336' : '#ddd'}`,
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                  {errors.email && (
                    <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${errors.phoneNumber ? '#f44336' : '#ddd'}`,
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                  {errors.phoneNumber && (
                    <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="City, Country"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={profileData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Gender
                  </label>
                  <select
                    value={profileData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Bio ({profileData.bio.length}/500)
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  placeholder="Tell us about yourself..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${errors.bio ? '#f44336' : '#ddd'}`,
                    borderRadius: '6px',
                    fontSize: '16px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                {errors.bio && (
                  <div style={{ color: '#f44336', fontSize: '12px', marginTop: '4px' }}>
                    {errors.bio}
                  </div>
                )}
              </div>

              {/* Languages */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Languages
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: '8px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: '#f9f9f9'
                }}>
                  {languageOptions.map((language) => (
                    <label key={language} style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={profileData.languages.includes(language)}
                        onChange={() => handleArrayChange('languages', language)}
                        style={{ 
                          marginRight: '8px',
                          width: '16px',
                          height: '16px',
                          accentColor: '#2196F3',
                          cursor: 'pointer',
                          transform: 'scale(1.2)',
                          WebkitAppearance: 'checkbox',
                          appearance: 'checkbox'
                        }}
                      />
                      {language}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'travel' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Travel Experience */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Travel Experience
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: '16px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      Experience Level
                    </label>
                    <select
                      value={profileData.travelExperience.level}
                      onChange={(e) => handleInputChange('travelExperience.level', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="experienced">Experienced</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      Countries Visited
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="200"
                      value={profileData.travelExperience.countriesVisited}
                      onChange={(e) => handleInputChange('travelExperience.countriesVisited', parseInt(e.target.value) || 0)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      Safety Priority
                    </label>
                    <select
                      value={profileData.travelExperience.safetyPriority}
                      onChange={(e) => handleInputChange('travelExperience.safetyPriority', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="low">Low - I'm very comfortable with risk</option>
                      <option value="medium">Medium - I take reasonable precautions</option>
                      <option value="high">High - Safety is very important to me</option>
                      <option value="very-high">Very High - Safety is my top priority</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Travel Styles */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Preferred Travel Styles
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: '8px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: '#f9f9f9'
                }}>
                  {travelStyles.map((style) => (
                    <label key={style} style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={profileData.travelExperience.preferredTravelStyle.includes(style)}
                        onChange={() => handleArrayChange('travelExperience.preferredTravelStyle', style)}
                        style={{ 
                          marginRight: '8px',
                          width: '16px',
                          height: '16px',
                          accentColor: '#2196F3',
                          cursor: 'pointer',
                          transform: 'scale(1.2)',
                          WebkitAppearance: 'checkbox',
                          appearance: 'checkbox'
                        }}
                      />
                      {style.charAt(0).toUpperCase() + style.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Interests */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Interests
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                  gap: '8px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: '#f9f9f9'
                }}>
                  {interestOptions.map((interest) => (
                    <label key={interest} style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={profileData.interests.includes(interest)}
                        onChange={() => handleArrayChange('interests', interest)}
                        style={{ 
                          marginRight: '8px',
                          width: '16px',
                          height: '16px',
                          accentColor: '#2196F3',
                          cursor: 'pointer',
                          transform: 'scale(1.2)',
                          WebkitAppearance: 'checkbox',
                          appearance: 'checkbox'
                        }}
                      />
                      {interest.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Profile Visibility */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Profile Visibility
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    Who can see your profile?
                  </label>
                  <select
                    value={profileData.privacy.profileVisibility}
                    onChange={(e) => handleInputChange('privacy.profileVisibility', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="public">Public - Anyone can see my profile</option>
                    <option value="friends">Friends - Only people I follow can see</option>
                    <option value="private">Private - Only I can see my profile</option>
                  </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.showRealName}
                      onChange={(e) => handleInputChange('privacy.showRealName', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Show real name
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Display your first and last name instead of just display name
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.showLocation}
                      onChange={(e) => handleInputChange('privacy.showLocation', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Show location
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Display your location on your profile
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.showAge}
                      onChange={(e) => handleInputChange('privacy.showAge', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Show age
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Display your age or age range on your profile
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.showReviewHistory}
                      onChange={(e) => handleInputChange('privacy.showReviewHistory', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Show review history
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Allow others to see your past reviews and ratings
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Interaction Settings */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Interaction Settings
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.allowDirectMessages}
                      onChange={(e) => handleInputChange('privacy.allowDirectMessages', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Allow direct messages
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Let other users send you private messages
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.allowFollowing}
                      onChange={(e) => handleInputChange('privacy.allowFollowing', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Allow following
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Let other users follow your reviews and activity
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Data Processing */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Data Processing
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.dataProcessing.analytics}
                      onChange={(e) => handleInputChange('privacy.dataProcessing.analytics', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Analytics
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Help us improve the platform by analyzing usage patterns
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.dataProcessing.marketing}
                      onChange={(e) => handleInputChange('privacy.dataProcessing.marketing', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Marketing
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Use my data to personalize marketing and recommendations
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.privacy.dataProcessing.thirdPartySharing}
                      onChange={(e) => handleInputChange('privacy.dataProcessing.thirdPartySharing', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Third-party sharing
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Allow sharing anonymized data with trusted partners
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Email Notifications */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Email Notifications
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.email.reviewResponses}
                      onChange={(e) => handleInputChange('notifications.email.reviewResponses', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Review responses
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Get notified when someone responds to your reviews
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.email.safetyAlerts}
                      onChange={(e) => handleInputChange('notifications.email.safetyAlerts', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Safety alerts
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Important safety notifications for your areas of interest
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.email.weeklyDigest}
                      onChange={(e) => handleInputChange('notifications.email.weeklyDigest', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Weekly digest
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Weekly summary of safety updates and new reviews
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.email.marketing}
                      onChange={(e) => handleInputChange('notifications.email.marketing', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Marketing emails
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Product updates, tips, and promotional content
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Push Notifications */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  Push Notifications
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.push.reviewResponses}
                      onChange={(e) => handleInputChange('notifications.push.reviewResponses', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Review responses
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Instant notifications for review interactions
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.push.safetyAlerts}
                      onChange={(e) => handleInputChange('notifications.push.safetyAlerts', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Safety alerts
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Urgent safety notifications for your current location
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.push.nearbyUpdates}
                      onChange={(e) => handleInputChange('notifications.push.nearbyUpdates', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Nearby updates
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        New reviews and safety updates for nearby locations
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* SMS Notifications */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                  SMS Notifications
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.sms.emergencyAlerts}
                      onChange={(e) => handleInputChange('notifications.sms.emergencyAlerts', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Emergency alerts
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Critical safety alerts sent via SMS
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '8px 0'
                  }}>
                    <input
                      type="checkbox"
                      checked={profileData.notifications.sms.verificationCodes}
                      onChange={(e) => handleInputChange('notifications.sms.verificationCodes', e.target.checked)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#2196F3',
                        cursor: 'pointer',
                        transform: 'scale(1.2)',
                        WebkitAppearance: 'checkbox',
                        appearance: 'checkbox'
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                        Verification codes
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Two-factor authentication and verification codes
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '0',
          marginTop: 'auto', // Push footer to bottom
          flexShrink: 0 // Prevent footer from shrinking
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Profile {calculateProfileCompletion()}% complete
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: isMobile ? '14px 24px' : '10px 20px',
                backgroundColor: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                minHeight: isMobile ? '44px' : 'auto', // WCAG touch target
                touchAction: 'manipulation'
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              style={{
                padding: isMobile ? '14px 24px' : '10px 20px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
                minHeight: isMobile ? '44px' : 'auto', // WCAG touch target
                touchAction: 'manipulation'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;